import { useEffect, useState } from "react";
import { useRole } from "@/contexts/RoleContext";
import { learnerJourney } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { CheckCircle2, HeartHandshake, Unlock, Map as MapIcon, Lock } from "lucide-react";

const STATUS_TONE = {
  pool_a_complete: { bg: "bg-emerald-50", border: "border-emerald-200", icon: CheckCircle2, color: "text-emerald-700", label: "Pool A complete" },
  pool_b:          { bg: "bg-sky-50",     border: "border-sky-200",     icon: CheckCircle2, color: "text-sky-700",     label: "Pool B in progress" },
  in_progress:     { bg: "bg-white",      border: "border-slate-200",   icon: MapIcon,      color: "text-slate-600",   label: "In progress" },
  stopped:         { bg: "bg-amber-50",   border: "border-amber-200",   icon: HeartHandshake, color: "text-amber-700", label: "Stopped — let's revisit" },
  hard_locked:     { bg: "bg-slate-100",  border: "border-slate-200",   icon: Lock,         color: "text-slate-500",   label: "Locked" },
};

export default function JourneyMap() {
  const { learner } = useRole();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!learner) return;
    learnerJourney(learner.learner_id).then(setData).catch(() => setData(null));
  }, [learner]);

  if (!data) return <div className="text-center py-20 text-slate-400" data-testid="journey-loading">Loading your journey…</div>;

  const nodes = data.sub_topic_nodes || [];
  // Group by subject
  const grouped = nodes.reduce((acc, n) => {
    acc[n.subject] = acc[n.subject] || [];
    acc[n.subject].push(n);
    return acc;
  }, {});

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8" data-testid="journey-map">
      <h1 className="font-display text-4xl md:text-5xl font-bold text-slate-800 mb-2">Your Journey Map</h1>
      <p className="text-slate-600 mb-8">A map of where you've been. Every node is a step you took. Stops are okay — they just mean we paused to grow stronger.</p>

      {Object.keys(grouped).length === 0 && (
        <Card className="rounded-3xl p-10 mit-shadow bg-white text-center text-slate-500" data-testid="journey-empty">
          You haven't started any sub-topics yet. Pick one from Home to begin your journey!
        </Card>
      )}

      {Object.entries(grouped).map(([subject, items]) => (
        <div key={subject} className="mb-10" data-testid={`journey-subject-${subject}`}>
          <h2 className="font-display text-2xl font-bold text-sky-700 mb-4">{subject}</h2>
          <div className="flex gap-3 overflow-x-auto pb-2">
            {items.map((n, i) => {
              const tone = STATUS_TONE[n.status] || STATUS_TONE.in_progress;
              const Icon = tone.icon;
              return (
                <div key={i} className="flex items-center" data-testid={`journey-node-${n.sub_topic}`}>
                  <div className={`min-w-[200px] max-w-[260px] rounded-3xl border-2 ${tone.border} ${tone.bg} p-4`}>
                    <div className="flex items-center gap-2">
                      <Icon className={`w-5 h-5 ${tone.color}`} />
                      <span className={`text-xs font-bold uppercase ${tone.color}`}>{tone.label}</span>
                    </div>
                    <div className={`font-display text-lg font-bold mt-1 ${n.status === "hard_locked" ? "text-slate-500" : "text-slate-800"}`}>{n.sub_topic}</div>
                    {n.status === "hard_locked" ? (
                      <div className="text-xs text-slate-500 mt-1.5" data-testid={`journey-locked-${n.sub_topic}`}>
                        Locked — complete <em className="not-italic font-semibold">{n.prerequisite_sub_topic}</em> to unlock, or ask your teacher.
                      </div>
                    ) : (
                      <div className="text-xs text-slate-500 mt-1">{n.attempts} questions • {n.stops_count > 0 ? `${n.stops_count} stop` : "no stops"}</div>
                    )}
                    {n.unlocked_by_teacher && (
                      <div className="text-xs text-sky-700 mt-1 flex items-center gap-1"><Unlock className="w-3 h-3" /> Teacher unlocked</div>
                    )}
                  </div>
                  {i < items.length - 1 && <div className="w-8 h-0.5 bg-sky-200 mx-1" />}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
